// Role-based fallback for permission checks, used when RBAC is not the authorization
// source for a deployment (REACT_APP_AUTH_MODE=local).
//
// The grants come from rbac/esb-admin.seed.json - the same file that provisions the RBAC
// service - rather than from rules written out here. That is the point of it: a deployment
// on RBAC and a deployment on local roles now answer from one description of who may do
// what, and adding a screen means adding it to the seed rather than to the seed and to a
// list in here that nobody remembers exists.
//
// It replaces a hand-maintained set of tiers, which is worth recording because every fault
// that set had was of one kind. It matched 'site-admin' while a local login writes 'admin',
// so site administrators got no console. It put operators in no tier at all, so they got
// none either. It granted the whole Viewers: prefix, so plain users were offered two screens
// the router then refused, and the alarm viewer's clear buttons besides. It granted anything
// outside that prefix to any tier, so Integrations appeared for people the screen turns away.
// Five faults, all of them a name or a prefix written in one place and meant in another.
//
// None of those are expressible here. A URN is granted to a role or it is not, and the seed
// says which.
//
// Two things are still decided in this file, because the seed cannot express them:
//
//   ROLE_ALIASES   the vocabularies differ. RBAC issues 'site-admin' in its app_role claims;
//                  login.js derives its roles from esb-data's user flags and writes
//                  UserRoles.ADMIN.label, which is 'admin'. Same tier, two names.
//   BASELINE_ROLE  a local session's roles are its alarm group names - 'eh_fm', 'security' -
//                  which are not seed roles, so such a session would match nothing at all.
//                  The legacy rule was that /user is reachable by anyone signed in, so every
//                  authenticated session is at least the seed's 'user'.
//
// Note this remains COARSER than RBAC: it cannot express per-user grants, only per-role ones.
// Granularity is what RBAC is for. This is the compatibility shim that lets one image serve
// deployments which have not adopted it.
//
// And it is not access control. It decides what the console offers. Protected gates /admin on
// role tier rather than on the URN, and esb-data checks that a token is valid rather than
// whose it is - so a screen withheld here is a screen not offered, not a screen defended.

import seed from '../rbac/esb-admin.seed.json';

/** RBAC's name for a tier, keyed by the name a local login writes. */
const ROLE_ALIASES = { admin: 'site-admin' };

/** What any authenticated session holds, whatever else it does. */
const BASELINE_ROLE = 'user';

/**
 * The seed's grants, as role name -> set of "urn|action".
 *
 * Built once at module load. A permission is a pair, not a URN: the same resource carries
 * read for one role and write for another, and collapsing that would handed out deletes with
 * views.
 */
const GRANTS = new Map(
    (seed.appRoles ?? []).map((role) => [
        role.name,
        new Set((role.permissions ?? []).map((p) => `${p.urn}|${p.action}`)),
    ])
);

/**
 * @param {string} urn    a URN from rbac/esb-admin.seed.json
 * @param {string[]} roles the roles on the current session (settings.roles)
 * @param {string} action  read / write / execute / acknowledge, as the call site asked it
 * @returns {boolean}
 */
export function roleAllows(urn, roles, action) {
    const held = Array.isArray(roles) ? roles : [];

    // Unauthenticated sessions get nothing, the baseline included.
    if (held.length === 0) {
        return false;
    }

    const granted = `${urn}|${action}`;

    return [...held.map((role) => ROLE_ALIASES[role] ?? role), BASELINE_ROLE]
        .some((role) => GRANTS.get(role)?.has(granted));
}

/** Which seed roles a session resolves to. Exported for the tests and for diagnosis. */
export function seedRolesFor(roles) {
    const held = Array.isArray(roles) ? roles : [];

    if (held.length === 0) {
        return [];
    }

    return [...held.map((role) => ROLE_ALIASES[role] ?? role), BASELINE_ROLE]
        .filter((role) => GRANTS.has(role));
}

export { ROLE_ALIASES, BASELINE_ROLE, GRANTS };
